/**
 * Pure snapshot-diff helpers for narrowing deep specTrack subscriptions
 * (EID-61).
 *
 * The providers (layer-provider, data-provider) observe deep spec changes by
 * serialising each spec entry to a JSON snapshot string inside a reactive
 * scope (the stringify walk reads every nested property through the store
 * proxy, registering fine-grained subscriptions). The snapshot strings are
 * then diffed against the last applied set so that only the entries whose
 * content actually changed are re-processed — instead of re-instantiating /
 * re-fetching everything on any patch.
 *
 * Kept dependency-free (no 'framework'/'utils' barrel imports) so it can be
 * unit tested per the repo testing note.
 */

export interface SnapshotEntry {
  id: string;
  /** Serialised content of the entry (typically JSON.stringify of the spec). */
  snapshot: string;
}

export interface SnapshotDiff {
  /** Ids that are new or whose snapshot differs from the applied one. */
  changedIds: string[];
  /** Ids present in the applied set but absent from the next entries. */
  removedIds: string[];
  /** Ids whose snapshot is identical to the applied one. */
  unchangedIds: string[];
  /** The next applied set (id → snapshot) to store once changes are applied. */
  nextById: Record<string, string>;
}

/**
 * Diff a list of snapshot entries against the previously applied snapshots.
 *
 * `forceAll` marks every present entry as changed regardless of snapshot
 * equality (used e.g. when an auth token rotates and every entry must be
 * re-processed with the new credentials).
 *
 * Duplicate ids in `entries` are collapsed last-wins, mirroring how a keyed
 * registry would store them.
 */
export const diffSnapshotEntries = (
  prevById: Record<string, string>,
  entries: SnapshotEntry[],
  options: { forceAll?: boolean } = {},
): SnapshotDiff => {
  const nextById: Record<string, string> = {};
  const changedIds: string[] = [];
  const unchangedIds: string[] = [];
  for (const entry of entries) {
    const seen = entry.id in nextById;
    nextById[entry.id] = entry.snapshot;
    if (seen) continue;
    if (!options.forceAll && prevById[entry.id] === entry.snapshot) {
      unchangedIds.push(entry.id);
    } else {
      changedIds.push(entry.id);
    }
  }
  // A duplicate id whose later snapshot differs from the applied one must be
  // reported as changed even if the first occurrence matched.
  for (const id of [...unchangedIds]) {
    if (nextById[id] !== prevById[id]) {
      unchangedIds.splice(unchangedIds.indexOf(id), 1);
      changedIds.push(id);
    }
  }
  const removedIds = Object.keys(prevById).filter((id) => !(id in nextById));
  return { changedIds, removedIds, unchangedIds, nextById };
};

/**
 * Return a new registry containing only the entries of `registry` whose key
 * is in `allowedIds`. Used by data-provider's data/refresh/dataSpec pruning
 * effect (EID-110 M1: dataSpec was previously left out of this pass, so a
 * removed datamesh source with a live-tail timefilter kept matching
 * needsRefresh() and was re-staged/re-queried forever).
 */
export const pruneByIds = <T>(
  registry: Record<string, T>,
  allowedIds: string[],
): Record<string, T> => {
  return Object.keys(registry).reduce(
    (obj, key) => {
      if (allowedIds.includes(key)) obj[key] = registry[key];
      return obj;
    },
    {} as Record<string, T>,
  );
};

/**
 * Whether a source id is still present in a list of live source specs (each
 * carrying an `id`). data-provider guards its post-await store writes with
 * this: a datamesh source can be removed from the spec (and pruned via
 * pruneByIds) while its stage/schema/query promise is still in flight, and
 * the resolving `.then` must not write the stale result back into the
 * data/refresh/dataSpec stores — that would resurrect a pruned entry. Worst
 * case a re-added dataSpec entry with a live-tail timefilter re-arms the
 * refresh flag, so the timer effect re-queries a removed source forever
 * (EID-110 M2).
 */
export const sourceStillPresent = (
  sources: { id: string }[],
  id: string,
): boolean => sources.some((s) => s.id === id);

/**
 * Equality comparator for createMemo({ equals }) over snapshot-entry lists:
 * two lists are equal when they contain the same ids with the same snapshots
 * in the same order. Lets a memo absorb upstream re-evaluations that did not
 * actually change the entries (e.g. a patch to an unrelated spec subtree that
 * recreated an array identity).
 */
export const sameSnapshotEntries = (
  a: SnapshotEntry[],
  b: SnapshotEntry[],
): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id || a[i].snapshot !== b[i].snapshot) return false;
  }
  return true;
};
